import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function About() {
  useEffect(() => {
    document.title = 'About Summit Appalachia Insurance | Middlesboro KY Independent Agency';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/about#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/about`,
      name: 'About Summit Appalachia Insurance | Middlesboro KY Independent Agency',
      description: 'Learn about Summit Appalachia Insurance — an independent insurance agency in Middlesboro, KY serving contractors, coal mining, trucking, and small businesses across KY, TN, VA, and WV.',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: {
        '@type': 'InsuranceAgency',
        name: 'Summit Appalachia Insurance',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1012 N 12th St',
          addressLocality: 'Middlesboro',
          addressRegion: 'KY',
          postalCode: '40965',
          addressCountry: 'US',
        },
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-about')?.remove(); };
  }, []);

  return (
    <div className="font-sans bg-[#0a0a0a]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/828a22527e888de6c636859f1adeca66.jpg"
            alt="About Summit Appalachia"
            className="w-full h-full object-cover object-top opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#c9a84c] cursor-pointer">Home</Link> / About Us
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-white uppercase">Who We Are</h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.3em] font-sans">Our Story</span>
              <h2 className="font-serif text-4xl md:text-5xl text-white mt-4 mb-6 uppercase leading-tight">
                Protecting What Matters Most
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-5 font-sans">
                At Summit Appalachia Insurance, we proudly serve the Tri-State area — Kentucky, Tennessee, and Virginia — providing honest and reliable insurance solutions to local families and businesses.
              </p>
              <p className="text-white/60 text-base leading-relaxed mb-5 font-sans">
                As an independent agency, we work for you. Our mission is simple: protect what matters most so you can focus on growing what matters next.
              </p>
              <p className="text-white/60 text-base leading-relaxed font-sans">
                We specialize in the unique risks faced by contractors, tradesmen, coal operations, and small business owners throughout the Appalachian region. We understand your industry because we live and work here too.
              </p>
            </div>
            <div className="relative">
              <div className="w-full h-[420px] rounded-md overflow-hidden border border-[#c9a84c]/20">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20insurance%20agent%20meeting%20with%20business%20owner%20contractor%20in%20office%20handshake%20trust%20professional%20setting%20warm%20lighting%20neutral%20background%20high%20quality%20photography&width=700&height=500&seq=aboutimg2026&orientation=landscape"
                  alt="Our team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#c9a84c] text-black p-6 rounded-md">
                <p className="font-serif text-4xl font-bold">50</p>
                <p className="text-xs font-bold uppercase tracking-wider mt-1">States Served</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Specialize In */}
      <section className="py-20 bg-[#111111] border-y border-[#c9a84c]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.3em] font-sans">Our Expertise</span>
            <h2 className="font-serif text-4xl md:text-5xl text-white mt-3 uppercase">Industries We Serve</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: 'ri-tools-line', label: 'Contractors' },
              { icon: 'ri-user-settings-line', label: 'Tradesmen' },
              { icon: 'ri-truck-line', label: 'Commercial Auto' },
              { icon: 'ri-building-line', label: 'Construction' },
              { icon: 'ri-store-2-line', label: 'Business Owners' },
              { icon: 'ri-shield-star-line', label: 'High-Risk Ops' },
            ].map(item => (
              <div key={item.label} className="bg-[#1a1a1a] border border-[#c9a84c]/10 rounded-md p-6 flex flex-col items-center text-center hover:border-[#c9a84c]/40 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-[#c9a84c] text-2xl`} />
                </div>
                <span className="text-white/70 text-xs font-bold uppercase tracking-wider font-sans">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.3em] font-sans">What Drives Us</span>
            <h2 className="font-serif text-4xl md:text-5xl text-white mt-3 uppercase">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: '01', title: 'Integrity', desc: 'We operate with complete transparency and honesty in every interaction. You\'ll always know exactly what your coverage includes and what it costs.' },
              { num: '02', title: 'Expertise', desc: 'We specialize in the unique risks of Appalachian industries. Our agents know your business because we\'re part of the same community.' },
              { num: '03', title: 'Fast Service', desc: 'When you need us, we\'re there. Fast quotes, fast claims, and an agent who picks up the phone — that\'s the Summit Appalachia promise.' },
            ].map(v => (
              <div key={v.num} className="bg-[#111111] border border-[#c9a84c]/15 rounded-md p-8">
                <span className="font-serif text-5xl text-[#c9a84c]/20 font-bold">{v.num}</span>
                <h3 className="font-serif text-2xl text-[#c9a84c] mt-3 mb-4 uppercase">{v.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed font-sans">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#c9a84c]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl text-black uppercase mb-4">Ready to Get Covered?</h2>
          <p className="text-black/70 font-sans mb-8">Contact us today and let&apos;s find the right policy for your business.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6062691190" className="px-8 py-4 bg-black text-[#c9a84c] font-bold text-sm uppercase tracking-widest rounded-md hover:bg-[#111] transition-colors whitespace-nowrap cursor-pointer font-sans">
              Call 606-269-1190
            </a>
            <Link to="/contact" className="px-8 py-4 border-2 border-black text-black font-bold text-sm uppercase tracking-widest rounded-md hover:bg-black hover:text-[#c9a84c] transition-colors whitespace-nowrap cursor-pointer font-sans">
              Send a Message
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
